import appInfo from "@common/appInfo";
import { type GenericMap } from "@common/declarations";
import { combineUrl, parseQueryString, updateQueryString } from "@common/lib/url";
import { Common } from "@common/types";
import { groupBy, isDate, random, trimStart } from "lodash";
import { type I18n, localize } from "./i18n";

/*
e.g.:
emailDescription
boardDescriptionPrefix/Label
boardDescriptionValue/Label
*/
const getDescriptions = <T>(opt: Common.Api.DropdownItem<T>): Common.OptionItemDescription[] =>
  Object.keys(opt)
    .filter((x) => x.includes("Description") && !x.includes("Value"))
    .map((x) => {
      if (x.includes("Prefix")) {
        const valuePropertyName = x.includes("Label")
          ? x.replace("PrefixLabel", "Value")
          : x.replace("Prefix", "Value");
        const valuePropertyNameWithLabel = x.includes("Label")
          ? x.replace("PrefixLabel", "ValueLabel")
          : x.replace("Prefix", "ValueLabel");
        return {
          label:
            opt[valuePropertyName] || opt[valuePropertyNameWithLabel]
              ? `${opt[x]}: ${opt[valuePropertyName] ?? opt[valuePropertyNameWithLabel]}`
              : (opt[x] as string),
          isHint: x.includes("Prefix")
        };
      }
      return { label: opt[x], isHint: false };
    });

const downloadUploadRequest = async (
  url: string,
  requestInfo?: RequestInit,
  withoutContentType?: boolean
): Promise<Response> => {
  if (!requestInfo) {
    requestInfo = {};
  }

  if (!requestInfo.headers) {
    requestInfo.headers = {};
  }

  if (!withoutContentType && !requestInfo.headers["Content-Type"] && !(requestInfo.body instanceof FormData)) {
    requestInfo.headers["Content-Type"] = "application/json";
  }

  const finalUrl = updateQueryString(url, parseQueryString(url)); // make sure qs is encoded
  return fetch(finalUrl, requestInfo);
};

type Error = {
  code: string;
  message: string;
  statusCode: number;
};
const extractError = async (result: Response): Promise<Error> => {
  const text = await result.text();
  // try extracting the error
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {}

  let code = parsed ? parsed.code || parsed.errorCode || parsed.Code : "";
  let message =
    (parsed ? parsed.Message || parsed.message || parsed.error : "") || text || "Unknown error has occurred";

  return { code, message, statusCode: result.status };
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  //Create anchor tag with local file and click it for download, then remove it
  const a = document.createElement("a");
  a.href = window.URL.createObjectURL(blob);
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export interface DownloadArgs {
  url: string;
  name?: string;
  method?: string;
  body?: any;
}

export const download = async ({ url, body, method, name }: DownloadArgs): Promise<void> => {
  const response = await downloadUploadRequest(
    url,
    {
      body: body ? JSON.stringify(body) : null,
      method
    },
    true
  );
  if (response.ok) {
    const blob = await response.blob();

    //Get the name of the downloaded file from the header
    const match = (response.headers.get("Content-Disposition") || "").match(/filename=([^;]+)/);
    const fileName = name ?? ((match ? match[1] : "") || blob["name"]);

    downloadBlob(blob, fileName);
  } else {
    const error = await extractError(response);

    throw new HttpError(error.statusCode, error.message);
  }
};

export const addQueryParameters = (url: string, parameters: object | undefined | null): string => {
  if (!parameters) return url;

  const allParameters = {
    ...parseQueryString(url),
    ...parameters
  };

  return updateQueryString(url, allParameters);
};

const toOptionItemsTree = <T extends Common.Primitive>(
  parent: Common.OptionItem<T>,
  dropdownItems: Common.Api.DropdownItem<T>[]
) => {
  const groupped = groupBy(dropdownItems, (x) => (x.parentValue ?? "").toString());

  const _toOptionItemsTree = (parent: Common.OptionItem<T>) => {
    const children = groupped[(parent.value ?? "").toString()];
    if (!children) return;

    parent.children = children.map((x) => ({
      ...x,
      label: x.caption,
      value: x.value as T,
      descriptions: getDescriptions(x)
    }));

    parent.children.forEach(_toOptionItemsTree);
  };

  _toOptionItemsTree(parent);
};

export const toOptionItems = <T extends Common.Primitive>(
  dropdownItems: Common.Api.DropdownItem<T>[]
): Common.OptionItem<T>[] => {
  const root: Common.OptionItem<T> = {
    parentValue: null,
    value: null as any,
    children: [],
    label: ""
  };
  toOptionItemsTree<T>(root, dropdownItems);
  return root.children || [];
};

export const addQueryParam = (url: string, key: string, value: any) =>
  value || value === 0 || value === ""
    ? `${url}${url.indexOf("?") >= 0 ? "&" : "?"}${encodeURI(`${key}=${isDate(value) ? value.toISOString() : value}`)}`
    : url;

// above code is obsolete

export class HttpError extends Error {
  public code: number;

  constructor(code: number, message: string) {
    super(message);

    this.code = code;
  }
}

export class ValidationError extends Error {
  public formFieldErrors: Common.GenericMap<string>;

  constructor(formFieldErrors: Common.GenericMap<string>) {
    super("Validation error");

    this.formFieldErrors = formFieldErrors;
  }
}

const adjustV1Url = (url: string): string => {
  const adjustedUrl = url.startsWith("http") ? url : `/${trimStart(url, "/")}`;
  return adjustedUrl.startsWith("http") || adjustedUrl.startsWith("/api")
    ? adjustedUrl
    : combineUrl("/api/v1", adjustedUrl);
};

const apiRequestBase = async <T>(
  i18n: I18n,
  url: string,
  requestInfo?: RequestInit,
  isJson: boolean = true
): Promise<T> => {
  const ri = {
    ...(requestInfo || {})
  };
  ri.headers = ri.headers ?? {};

  if (isJson) ri.headers["Content-Type"] = "application/json";

  const result = await fetch(adjustV1Url(url), ri);
  if (result.headers.get("Content-Type")?.startsWith("application/json")) {
    if (result.status === 401) {
      throw new HttpError(result.status, "Unauthorized");
    }

    const r = await result.json();

    if (result.status === 400 && r.formFieldErrors) {
      throw new ValidationError(r.formFieldErrors);
    }

    if (result.status >= 400) {
      throw new HttpError(result.status, i18n[r.title ?? r.error ?? "somethingWentWrong"]);
    }

    // NOTE: API does not translate
    // apply translations to labels
    r && localize(i18n, r);

    return r;
  }

  //Get the name of the downloaded file from the header
  const match = (result.headers.get("Content-Disposition") || "").match(/filename="?([^";]+)/);
  if (match) {
    const blob = await result.blob();

    const fileName = (match ? match[1] : "") || blob["name"];

    //Create anchor tag with local file and click it for download, then remove it
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  if (result.status >= 400) {
    throw new Error(result.statusText);
  }

  return result.text() as any;
};

let requestPromises: GenericMap<Promise<any>> = {};
type ApiRequestInit = Omit<RequestInit, "cache"> & {
  cache?: CacheType;
};
export const apiRequest = async <T>(
  i18n: I18n,
  url: string,
  requestInfo?: ApiRequestInit,
  isJson: boolean = true
): Promise<T> => {
  const { cache, ...ri } = requestInfo ?? {};
  if (cache === "memory") {
    if ((requestInfo?.method ?? "GET") === "GET") {
      if (!(url in requestPromises)) {
        requestPromises[url] = apiRequestBase(i18n, url, ri, isJson);
      }

      return requestPromises[url];
    }

    return apiRequestBase(i18n, url, ri, isJson);
  }

  return apiRequestBase(i18n, url, { cache, ...ri }, isJson);
};

export type CacheType = RequestCache | "memory";

export const mapV1DropdownItem = <T = Common.Primitive>({
  caption,
  label,
  groupCaption,
  groupLabel,
  ...x
}: Common.Api.V1.DropdownItem<T>): Common.Api.DropdownItem<T> => ({
  caption: caption ?? label,
  group: groupCaption ?? groupLabel ?? undefined,
  ...x
});
const apiV1 = {
  delete: <T>(i18n: I18n, path: string, qs?: object): Promise<T> =>
    apiRequest(i18n, addQueryParameters(path, qs), { method: "DELETE" }, true),
  download: (path: string) => download({ url: adjustV1Url(path) }),
  upload: async <R = any>(i18n: I18n, path: string, file: File): Promise<R> => {
    const formData = new FormData();
    formData.append(file.name, file);

    const result = await downloadUploadRequest(path, {
      body: formData,
      method: "POST"
    });

    const json = await result.json();
    json && localize(i18n, json);
    return <R>json;
  },
  get: <T>(i18n: I18n, path: string, qs?: object, cache?: CacheType): Promise<T> =>
    apiRequest(i18n, addQueryParameters(path, qs), { cache, method: "GET" }, true),
  getDropdownItems: async <T extends Common.Primitive>(
    i18n: I18n,
    moduleName: string,
    tableName: string,
    parameters: object = {},
    cache?: CacheType | boolean,
    isHost?: boolean
  ): Promise<Common.OptionItem<T>[]> => {
    let url = `/${moduleName}/option/${tableName}`;
    if (isHost) {
      url = combineUrl(appInfo.host.url, adjustV1Url(url));
    }

    const res = await apiRequest<Common.Api.V1.DropdownItem<T>[]>(i18n, addQueryParameters(url, parameters), {
      cache: cache === true ? "force-cache" : cache === false ? "no-cache" : cache
    });

    return toOptionItems<T>(res.map(mapV1DropdownItem));
  },
  post: <T>(i18n: I18n, path: string, data?: object): Promise<T> =>
    apiRequest(i18n, path, { method: "POST", body: JSON.stringify(data ?? {}) }, true),
  postFile: <T>(i18n: I18n, path: string, file: File, qs?: object): Promise<T> => {
    const body = new FormData();
    body.append("file", file);

    return apiRequest(i18n, addQueryParameters(path, qs), { method: "POST", body }, false);
  },
  put: <T>(i18n: I18n, path: string, data?: object): Promise<T> =>
    apiRequest(i18n, path, { method: "PUT", body: JSON.stringify(data ?? {}) }, true)
};
export default apiV1;

export const generateToken = (): string => {
  let currentDate = new Date();
  const hours = currentDate.getUTCHours().toString().padStart(2, "0");
  const minutes = currentDate.getUTCMinutes().toString().padStart(2, "0");
  const seconds = currentDate.getUTCSeconds().toString().padStart(2, "0");

  const part1 = random(1, 9);
  const part2 = random(500, 1000) * 2;
  const part3 = random(50, 100) * 5;
  const part4 = random(10, 99);

  return `${part1}${seconds}${part2}${minutes}${part3}${hours}${part4}`;
};

export const getApiPrefix = (isHost: boolean = false) => (isHost ? "/api/host/v1" : "");

export const getApiAdminPrefix = (isHost: boolean = false) => (isHost ? "/api/host/admin/v1" : "/api/admin/v1");

export const apiHostChildPrefix = "/api/host/child/v1";
